import { CommonModule } from '@angular/common';
import { Component, computed, inject, Input, Signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MoneyTrackerSetup, MoneyTrackerAction } from '@app/_interfaces/money-tracker.interface';
import { MoneyTrackerStore } from '@app/_stores';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { ParseTimeLocalPipe } from '@app/_pipes/parse-time-local.pipe';
import { ParseRulePipe } from '@app/_pipes/parse-rule.pipe';
// MoneyTransactionFormComponent loaded dynamically to break circular dependency
import { Entry } from '@app/_models/entry.model';
import { MoneySchedule } from '@app/_interfaces/money-tracker.interface';
import { CacheService } from '@app/_services/cache.service';
import { ParseTextPipe } from '@app/_pipes/parse-text.pipe';
import { ParseMoneyPipe } from '@app/_pipes/parse-money.pipe';
import { MeUserAvatarComponent } from '../../../common/account/me-user-avatar/me-user-avatar.component';
import { SettingsService } from '@app/_services/settings.service';
import { MapService } from '@app/_services/map.service';
import { Attachment } from '@app/_interfaces/generic.interface';
import { CollaboraterRole } from '@app/_types/generic.type';
import { getRoleByUid, getCollaboratorCount } from '@app/_utils/members-model.utils';
import { ModalService } from '@app/_services/modal.service';
import { DependencyService } from '@app/_services/dependency.service';

@Component({
    selector: 'app-money-tracker-block',
    imports: [
        CommonModule,
        SvgComponent,
        ParseTextPipe,
        ParseMoneyPipe,
        ParseTimeLocalPipe,
        ParseRulePipe,
        MeUserAvatarComponent
    ],
    templateUrl: './money-tracker-block.component.html',
    styleUrl: './money-tracker-block.component.scss'
})

export class MoneyTrackerBlockComponent {

  readonly moneyStore = inject(MoneyTrackerStore);
  @Input() show: string[] = [];
  @Input() blockClass: string = '';
  @Input() entry!: Entry;
  @Input() descriptionType: string = 'none';
  @Input() isLabel: boolean = true;
  @Input() isSetup: boolean = false;

  /**
   * Whether this entity is a virtual schedule occurrence (no concrete transaction yet).
   * Entry.isVirtual is pre-computed by resolvers.
   */
  isVirtual: Signal<boolean> = computed(() => {
    return this.entry.isVirtual;
  });

  setup: Signal<MoneyTrackerSetup> = computed(() => {
    return this.moneyStore.idToSetup()[this.entry.setupId];
  });

  transaction: Signal<MoneyTrackerAction> = computed(() => {
    // Try to find a concrete transaction via actionId
    if (this.entry.actionId) {
      const concrete = this.moneyStore.idToTransaction()[this.entry.actionId];
      if (concrete) return concrete;
    }

    // Virtual entry: get or create a transaction from the schedule data
    if (this.entry.setupId && this.entry.date) {
      return this.moneyStore.getOrCreateTransaction(
        this.entry.setupId,
        this.entry.date,
        this.entry.scheduleId
      );
    }

    // Fallback: should not happen, but return a minimal transaction
    return this.moneyStore.idToTransaction()[this.entry.actionId || ''];
  });

  schedule: Signal<MoneySchedule | null> = computed(() => {
    const scheduleId = this.entry?.scheduleId;
    if (!scheduleId) return null;
    const s = this.setup();
    if (!s?.schedules) return null;
    return s.schedules[scheduleId] || null;
  });

  hashtags: Signal<string[]> = computed(() => {
    const setupTags = this.setup()?.tags || [];
    const txnTags = this.transaction() ? this.transaction()!.tags : [];
    const tags = [...setupTags, ...txnTags];
    const uniqueTags = [...new Set(tags)];
    return uniqueTags;
  });

  attachments: Signal<Attachment[]> = computed(() => {
    return Object.values(this.transaction()?.attachments || {}).filter(atch => atch.deletedAt === null);
  });

  role: Signal<CollaboraterRole> = computed(() => {
    const s = this.setup();
    if (!s) return 'viewer';
    if (s.uid === this.cc.user.uid) {
      return 'owner';
    } else if (s.members) {
      return getRoleByUid(s.members, this.cc.user.uid) || 'viewer';
    } else {
      return 'viewer';
    }
  });
  collabCount: Signal<number> = computed(() => {
    return getCollaboratorCount(this.setup()?.members);
  });
  roleMap: Signal<Record<string, string>> = this.mapService.collaboratorRoleMap;

  constructor(public dialog: MatDialog, public cc: CacheService, public ss: SettingsService, private mapService: MapService, private modalService: ModalService, public ds: DependencyService) {

  }

  async openTransaction() {
    // Dynamic import to break circular dependency:
    // money-tracker-block -> money-transaction-form -> money-tracker-setup-form -> money-transaction-entities
    const { MoneyTransactionFormComponent } = await import('../money-transaction-form/money-transaction-form.component');

    const txn = this.transaction();
    const virtual = this.isVirtual();

    const confirmDialog = this.modalService.openLevel1(
      MoneyTransactionFormComponent,
      {
        // Virtual entries open in 'new' mode pre-filled from schedule data
        mode: virtual ? 'new' : 'edit',
        value: txn,
        transactionType: txn?.transactionType || 'expense',
        scheduleId: this.entry?.scheduleId || null,
        setupId: this.entry?.setupId || null,
        dueDate: this.entry?.date || null,
      },
      { disableClose: true, autoFocus: false }
    );
  }
}
